import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { GitPullRequest, Loader2, Check, X, Clock, CheckCircle2, XCircle, Table2, BarChart3 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { DiffSection, AttrChangeCard } from '@/components/CompareDiff';

const STATUS_TABS = [
  { key: 'pending', label: 'Pending', icon: Clock },
  { key: 'approved', label: 'Approved', icon: CheckCircle2 },
  { key: 'rejected', label: 'Rejected', icon: XCircle },
];

function ChangeListDetail({ cl }) {
  const d = cl.diff || {};
  if (cl.resource_type === 'semantic_model') {
    return (
      <div className="space-y-3 mt-3">
        <AttrChangeCard title="Model Metadata" icon={Table2} changes={d.model_attributes} testid="cl-metadata" />
        {d.entities && <DiffSection title="Entities" icon={Table2} diff={d.entities} />}
        {d.relationships && <DiffSection title="Relationships" icon={Table2} diff={d.relationships} />}
        {d.measures && <DiffSection title="Measures" icon={Table2} diff={d.measures} />}
      </div>
    );
  }
  return (
    <div className="space-y-3 mt-3">
      <AttrChangeCard title="Insight Metadata" icon={BarChart3} changes={d.report_attributes} testid="cl-metadata" />
      <AttrChangeCard title="Query" icon={BarChart3} changes={d.query} testid="cl-query" />
      {d.visuals && <DiffSection title="Visuals" icon={BarChart3} diff={d.visuals} />}
      <AttrChangeCard title="Display Settings" icon={BarChart3} changes={d.settings} testid="cl-settings" />
    </div>
  );
}

export default function ChangeListsPage() {
  const [status, setStatus] = useState('pending');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [acting, setActing] = useState(null);

  const load = async (s = status) => {
    setLoading(true);
    try { const r = await api.listChangeLists(s); setItems(r.change_lists || []); }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(status);   }, [status]);

  const decide = async (cl, decision) => {
    setActing(cl.change_list_id);
    try {
      await api.decideChangeList(cl.change_list_id, decision);
      toast.success(`Change-list ${decision === 'approve' ? 'approved' : 'rejected'}`);
      await load(status);
    } catch (e) { toast.error(e.message); }
    finally { setActing(null); }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="change-lists-page">
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-primary/10 border border-primary/20"><GitPullRequest className="w-6 h-6 text-primary" /></div>
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight text-foreground">Change Requests</h1>
          <p className="text-sm text-muted-foreground mt-1">Review and approve promotion change-lists created from model & insight comparisons.</p>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-border/30">
        {STATUS_TABS.map(t => (
          <button key={t.key} onClick={() => setStatus(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ${status === t.key ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            data-testid={`cl-tab-${t.key}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-sm text-muted-foreground" data-testid="cl-empty">No {status} change requests.</div>
      ) : (
        <div className="space-y-3">
          {items.map(cl => {
            const name = (cl.right || {}).name || cl.resource_type;
            const isOpen = expanded === cl.change_list_id;
            return (
              <Card key={cl.change_list_id} className="glass-card rounded-2xl border-border/50 overflow-hidden" data-testid={`cl-item-${cl.change_list_id}`}>
                <div className="px-5 py-3.5 flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[9px] capitalize">{cl.resource_type.replace('_', ' ')}</Badge>
                      <span className="text-sm font-semibold text-foreground truncate">{name}</span>
                      <Badge className="text-[9px] bg-primary/15 text-primary border-primary/20">→ {cl.target_stage}</Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      by {cl.requested_by} · {cl.note || 'no note'}
                    </p>
                    {cl.status === 'approved' && cl.promotion_result && (
                      cl.promotion_result.executed
                        ? <p className="text-[11px] text-emerald-400 mt-0.5" data-testid={`cl-promoted-${cl.change_list_id}`}>✓ Promoted to {cl.promotion_result.stage}</p>
                        : <p className="text-[11px] text-muted-foreground/70 mt-0.5">Recorded for audit · {cl.promotion_result.reason}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setExpanded(isOpen ? null : cl.change_list_id)} className="text-xs" data-testid={`cl-toggle-${cl.change_list_id}`}>
                      {isOpen ? 'Hide' : 'View'} diff
                    </Button>
                    {cl.status === 'pending' ? (
                      <>
                        <Button size="sm" variant="outline" onClick={() => decide(cl, 'reject')} disabled={acting === cl.change_list_id} className="rounded-lg text-rose-400 border-rose-500/30 hover:bg-rose-500/10" data-testid={`cl-reject-${cl.change_list_id}`}><X className="w-3.5 h-3.5 mr-1" /> Reject</Button>
                        <Button size="sm" onClick={() => decide(cl, 'approve')} disabled={acting === cl.change_list_id} className="rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white" data-testid={`cl-approve-${cl.change_list_id}`}><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                      </>
                    ) : (
                      <Badge className={cl.status === 'approved' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' : 'bg-rose-500/15 text-rose-400 border-rose-500/20'}>
                        {cl.status} {cl.decided_by ? `by ${cl.decided_by}` : ''}
                      </Badge>
                    )}
                  </div>
                </div>
                {isOpen && <div className="px-5 pb-5 border-t border-border/20 pt-3"><ChangeListDetail cl={cl} /></div>}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
